// import React, { useState } from "react";
// import { Sidebar } from "./Sidebar";
// import { Footer } from "./Footer";
// import { useAuth } from "../contexts/AuthContext";
// import { NotificationBell } from "./NotificationsBell";
// import { SettingsDropdown } from "./SettingsDropdown";
// import { DirectMessageModal } from "./DirectMessage";
// import { Menu, X, MessageCircle } from "lucide-react";

// export default function Layout({ children }: { children: React.ReactNode }) {
//   const { logout } = useAuth();

//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [isMinimized, setIsMinimized] = useState(false);
//   const [isMessageOpen, setIsMessageOpen] = useState(false);
//   const [unreadCount, setUnreadCount] = useState(0);

//    const SAFE_TOP = 32;
//   const SAFE_BOTTOM = 0;
//   const isMobile = false;

//   return (
//     <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      
//       {/* ✅ Desktop Sidebar */}
//       <div
//         className={`hidden md:flex transition-all duration-300 ${
//           isMinimized ? "w-20" : "w-64"
//         }`}
//       >
//         <Sidebar
//           isOpen={true}
//           onClose={() => {}}
//           isMinimized={isMinimized}
//           setIsMinimized={setIsMinimized}
//         />
//       </div>

//       {/* ✅ Mobile Sidebar */}
//       <div className="md:hidden">
//         <Sidebar
//           isOpen={sidebarOpen}
//           onClose={() => setSidebarOpen(false)}
//           isMinimized={false}
//           setIsMinimized={() => {}}
//         />
//       </div>

//       {/* ✅ Right side */}
//       <div className="flex flex-col flex-1 w-full">
        
//         {/* Header */}
//         <header className="flex justify-between items-center px-4 sm:px-6 py-3 border-b bg-white dark:bg-gray-800 shadow-sm">
//            style={{ marginTop: isMobile ? SAFE_TOP : 0 }}
//           <button
//             onClick={() => setSidebarOpen(!sidebarOpen)}
//             className="md:hidden"
//           >
//             {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
//           </button>

//           <div className="flex items-center gap-3 ml-auto">
//             <button
//               onClick={() => setIsMessageOpen(true)}
//               className="p-2 hover:bg-gray-100 rounded-lg"
//             >
//               <MessageCircle size={20} />
//             </button>

//             <NotificationBell onUnreadCountChange={setUnreadCount} />

//             <SettingsDropdown onLogout={logout} />
//           </div>
//         </header>

//         {/* Main */}
//         <main className="flex-1 overflow-auto">
//           <div className="w-full px-4 sm:px-6 lg:px-8 pb-16">
//             {children}
//           </div>
//         </main>

//         <Footer />
//       </div>

//       {/* Modal */}
//       <DirectMessageModal
//         isOpen={isMessageOpen}
//         onClose={() => setIsMessageOpen(false)}
//       />
//     </div>
//   );
// }

import React, { useEffect, useRef, useState } from "react";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import { useAuth } from "../contexts/AuthContext";
import { NotificationBell } from "./NotificationsBell";
import { SettingsDropdown } from "./SettingsDropdown";
import { DirectMessageModal } from "./DirectMessage";
import { Menu, X, MessageCircle, MessageSquare, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getChatWsUrl, listConversations, listNotifications, markSeen } from "../api/chat";
import type { ChatNotification, Conversation } from "../api/chat";
import { jwtDecode } from "jwt-decode";

function getCurrentUserId(): string | null {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
  if (!token) return null;
  try {
    const d: any = jwtDecode(token);
    return d.id || d.user_id || d.sub || null;
  } catch { return null; }
}

// ── Chat notification panel ───────────────────────────────────────────────────
function ChatNotificationBadge() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState<Conversation[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchConvs = async () => {
    try {
      const list = await listConversations();
      setConvs(list);
      const total = list.reduce((s: number, c: any) => s + (c.unread_count || 0), 0);
      setUnread(total);
    } catch {}
  };

  useEffect(() => {
    fetchConvs();
    const interval = setInterval(fetchConvs, 60_000);
    const handler = (e: Event) => {
      const count = (e as CustomEvent).detail.count as number;
      setUnread(count);
      if (count === 0) {
        // Clear all local unread counts immediately — don't refetch (would race with stale API data)
        setConvs(prev => prev.map(c => ({ ...c, unread_count: 0 })));
      } else {
        // New messages arrived — refresh the list to show them
        fetchConvs();
      }
    };
    window.addEventListener('chat:unread', handler);
    return () => { clearInterval(interval); window.removeEventListener('chat:unread', handler); };
  }, []); // eslint-disable-line

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unreadConvs = convs
    .filter(c => (c.unread_count || 0) > 0)
    .sort((a, b) => new Date(b.last_activity || '').getTime() - new Date(a.last_activity || '').getTime())
    .slice(0, 10);

  const openConv = (id: string, messageId?: string | null) => {
    setOpen(false);
    const qs = messageId ? `?open=${id}&msg=${messageId}` : `?open=${id}`;
    navigate(`/chat${qs}`);
  };

  const currentUserId = getCurrentUserId();

  const getDisplayName = (c: Conversation) => {
    if (c.type !== 'dm') return c.name || 'Group Chat';
    const other = c.members?.find((m: any) => m.user_id !== currentUserId && m.name)
      || c.members?.find((m: any) => m.name);
    return other?.name || 'Direct Message';
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className={`relative p-2 rounded-lg transition-colors ${open ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
        title="Messages"
      >
        <MessageCircle size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[17px] h-[17px] flex items-center justify-center leading-none px-0.5">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-bold text-gray-900 text-sm">Messages</span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                  {unread} unread
                </span>
              )}
              <button
                onClick={() => { setOpen(false); navigate('/chat'); }}
                className="text-xs text-blue-600 hover:underline font-medium"
              >
                Open Chat
              </button>
            </div>
          </div>

          {/* Conversation list */}
          <div className="max-h-80 overflow-y-auto">
            {unreadConvs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <MessageSquare size={28} className="text-gray-300 mb-2" />
                <p className="text-xs text-gray-400 font-medium">No unread messages</p>
              </div>
            ) : (
              unreadConvs.map(c => (
                <button
                  key={c.id}
                  onClick={() => openConv(c.id, c.last_message?.id || null)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    {c.type === 'dm' ? (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                        {(getDisplayName(c)[0] || '?').toUpperCase()}
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
                        <Users size={16} className="text-white" />
                      </div>
                    )}
                    {(c.unread_count || 0) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 bg-green-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-0.5 border-2 border-white">
                        {(c.unread_count || 0) > 99 ? '99+' : c.unread_count}
                      </span>
                    )}
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{getDisplayName(c)}</p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {c.last_message?.is_deleted
                        ? 'Message deleted'
                        : c.last_message?.content || (c.last_message ? '📎 Attachment' : 'No messages')}
                    </p>
                  </div>

                  {/* Time */}
                  {c.last_message && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {new Date(c.last_message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => { setOpen(false); navigate('/chat'); }}
              className="w-full text-center text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors py-0.5"
            >
              View all conversations →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { logout , clockOut} = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const SAFE_TOP = 32;
  const SAFE_BOTTOM = 0;
  const isMobile = false;

  return (
    <div 
      className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden" 
      style={{ marginTop: isMobile ? SAFE_TOP : 0 }}
    >
      
      {/* ✅ Desktop Sidebar */}
      <div
        className={`hidden md:flex transition-all duration-300 ${
          isMinimized ? "w-20" : "w-64"
        }`}
      >
        <Sidebar
          isOpen={true}
          onClose={() => {}}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
        />
      </div>

      {/* ✅ Mobile Sidebar */}
      <div className="md:hidden">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isMinimized={false}
          setIsMinimized={() => {}}
        />
      </div>

      {/* ✅ Right side */}
      <div className="flex flex-col flex-1 min-w-0">
        
        {/* Header */}
        <header className="flex justify-between items-center px-4 sm:px-6 py-3 border-b bg-white dark:bg-gray-800 shadow-sm">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="flex items-center gap-3 ml-auto">
            <ChatNotificationBadge />

            <NotificationBell onUnreadCountChange={setUnreadCount} />

            <SettingsDropdown onLogout={logout} />

             {isMobile && (
                <button
                  onClick={clockOut}
                  className="px-3 py-1 rounded-lg bg-red-500 text-white text-sm 
                    active:scale-[0.99] active:bg-red-600 
                    transition-all duration-150 ease-in-out
                    touch-manipulation select-none"
                >
                  Clock Out
                </button>
              )}
            </div>
          </header>

        {/* Main */}
        <main className="flex-1 overflow-auto" style={{ paddingBottom: isMobile ? SAFE_BOTTOM : 0 }}>
          <div className="w-full px-4 sm:px-6 lg:px-8 pb-16">
            {children}
          </div>
        </main>

        <Footer />
      </div>

      {/* Modal */}
      <DirectMessageModal
        isOpen={isMessageOpen}
        onClose={() => setIsMessageOpen(false)}
      />
    </div>
  );
}
